import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import dayjs from 'dayjs';

import { useDownloadCertPdf, useDownloadReportPdf } from '../api/mutations';
import type { DownloadTarget } from '../api/queries';
import { useReportDataQuery } from '../api/queries';

// `fromLink`: opened from a ?device_id= link, which goes straight to the download step.
type LookedUp = DownloadTarget & { fromLink: boolean };

const dayOf = (time?: string) => (time ? dayjs(time).format('YYYY-MM-DD') : 'unknown');

export default function useDownloadResources({ initialDeviceId = '', initialIndex = -1 }: any = {}) {
    const [inputValue, setInputValue] = useState(initialDeviceId || '');
    const [target, setTarget] = useState<LookedUp | null>(() =>
        initialDeviceId
            ? { deviceId: initialDeviceId, index: Number.isFinite(initialIndex) ? initialIndex : -1, fromLink: true }
            : null
    );

    const { i18n } = useTranslation();
    const report = useReportDataQuery(target);
    const reportPdf = useDownloadReportPdf();
    const certPdf = useDownloadCertPdf();
    const { refetch } = report;

    // undefined until the lookup answers; null when the device has no record.
    const data = report.data;
    const reportData = data?.reportData ?? null;
    const certData = data?.certData ?? null;
    const deviceId = target?.deviceId ?? '';
    const hasError = Boolean(deviceId) && !report.isFetching && (report.isError || data === null);
    const page = target && (target.fromLink || data) ? 2 : 1;

    let message = 'Loading data...';
    if (reportPdf.isPending) message = `Preparing to download Data-report-${deviceId}.pdf`;
    else if (certPdf.isPending) message = `Preparing to download Calibration-certificate-${deviceId}.pdf`;
    const isPreparing = report.isFetching || reportPdf.isPending || certPdf.isPending;

    const handleConfirm = useCallback(() => {
        // Confirming the same device again asks the server again, e.g. after a failed lookup.
        if (target && target.deviceId === inputValue && target.index === -1) refetch();
        setTarget({ deviceId: inputValue, index: -1, fromLink: false });
    }, [inputValue, target, refetch]);

    const handleReset = useCallback(() => {
        setTarget(null);
        setInputValue('');
    }, []);

    const { mutate: saveReport } = reportPdf;
    const downloadReport = useCallback(() => {
        if (!deviceId) return;
        const fileName = `Data-report-${deviceId}-${dayOf(reportData?.startTime)}-to-${dayOf(reportData?.endTime)}.pdf`;
        saveReport({ deviceId, language: i18n.language, fileName });
    }, [deviceId, reportData, i18n.language, saveReport]);

    const { mutate: saveCert } = certPdf;
    const downloadCert = useCallback(() => {
        if (!deviceId) return;
        saveCert({ deviceId, language: i18n.language, fileName: `Calibration-certificate-${deviceId}.pdf` });
    }, [deviceId, i18n.language, saveCert]);

    const state = useMemo(
        () => ({
            inputValue,
            page,
            lockInput: Boolean(data),
            reportReady: Boolean(reportData),
            certReady: Boolean(certData),
            reportData,
            certData,
            isPreparing,
            message,
            hasError,
        }),
        [inputValue, page, data, reportData, certData, isPreparing, message, hasError]
    );

    const actions = useMemo(
        () => ({
            setInputValue,
            confirmDevice: handleConfirm,
            reset: handleReset,
            downloadReport,
            downloadCert,
        }),
        [handleConfirm, handleReset, downloadReport, downloadCert]
    );

    return { state, actions };
}
